import type { ReactNode } from "react";
import { createContext, useContext, useState } from "react";

type SidebarState = "expanded" | "collapsed";

interface SidebarContextType {
  state: SidebarState;
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
  isCollapsed: boolean;
}

const SidebarContext = createContext<SidebarContextType | null>(null);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(true);
  const [openMobile, setOpenMobile] = useState(false);
  const isMobile = false;

  const toggleSidebar = () => setOpen(!open);

  const state: SidebarState = open ? "expanded" : "collapsed";
  const isCollapsed = state === "collapsed";

  return (
    <SidebarContext.Provider
      value={{
        state,
        open,
        setOpen,
        openMobile,
        setOpenMobile,
        isMobile,
        toggleSidebar,
        isCollapsed,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

interface SidebarProps {
  children: ReactNode;
  collapsible?: "icon" | "none";
  className?: string;
}

export const Sidebar = ({ children, className = "" }: SidebarProps) => {
  const { isCollapsed } = useSidebar();

  return (
    <aside
      className={`left-0 top-0 h-screen bg-[hsl(var(--sidebar-background))] transition-all duration-300 ease-in-out z-40 flex flex-col ${
        isCollapsed ? "w-16" : "w-64"
      } ${className}`}
    >
      {children}
    </aside>
  );
};

interface SidebarHeaderProps {
  children: ReactNode;
  className?: string;
}

export const SidebarHeader = ({
  children,
  className = "",
}: SidebarHeaderProps) => {
  return <div className={className}>{children}</div>;
};

interface SidebarContentProps {
  children: ReactNode;
  className?: string;
}

export const SidebarContent = ({
  children,
  className = "",
}: SidebarContentProps) => {
  return (
    <div className={`flex-1 overflow-y-auto ${className}`}>{children}</div>
  );
};

interface SidebarFooterProps {
  children: ReactNode;
  className?: string;
}

export const SidebarFooter = ({
  children,
  className = "",
}: SidebarFooterProps) => {
  return <div className={className}>{children}</div>;
};

interface SidebarGroupProps {
  children: ReactNode;
  className?: string;
}

export const SidebarGroup = ({
  children,
  className = "",
}: SidebarGroupProps) => {
  return <div className={className}>{children}</div>;
};

interface SidebarGroupLabelProps {
  children: ReactNode;
  className?: string;
}

export const SidebarGroupLabel = ({
  children,
  className = "",
}: SidebarGroupLabelProps) => {
  return <div className={className}>{children}</div>;
};

interface SidebarGroupContentProps {
  children: ReactNode;
  className?: string;
}

export const SidebarGroupContent = ({
  children,
  className = "",
}: SidebarGroupContentProps) => {
  return <div className={className}>{children}</div>;
};

interface SidebarMenuProps {
  children: ReactNode;
  className?: string;
}

export const SidebarMenu = ({ children, className = "" }: SidebarMenuProps) => {
  return <ul className={`space-y-1 ${className}`}>{children}</ul>;
};

interface SidebarMenuItemProps {
  children: ReactNode;
  className?: string;
}

export const SidebarMenuItem = ({
  children,
  className = "",
}: SidebarMenuItemProps) => {
  return <li className={className}>{children}</li>;
};

interface SidebarMenuButtonProps {
  children: ReactNode;
  asChild?: boolean;
  isActive?: boolean;
  className?: string;
}

export const SidebarMenuButton = ({
  children,
  asChild,
  isActive = false,
  className = "",
}: SidebarMenuButtonProps) => {
  const { isCollapsed } = useSidebar();

  const baseClasses = `flex items-center rounded-lg transition-all duration-200 group relative px-3 py-2 ${
    isCollapsed ? "justify-center" : "justify-start"
  } ${
    isActive
      ? "bg-[hsl(var(--sidebar-accent))] text-[hsl(var(--sidebar-accent-foreground))] shadow-lg"
      : "text-gray-100 hover:bg-white/10 hover:text-white font-medium"
  } ${className}`;

  if (asChild) {
    return <div className={baseClasses}>{children}</div>;
  }

  return <button className={baseClasses}>{children}</button>;
};
